import React from 'react';
import { getIconForFile } from 'vscode-icons-js';
import { Icon } from '../Icon';
import * as PhosphorIcons from '@phosphor-icons/react';

interface FileIconProps {
  filename: string;
  entityType?: string; // Add entity type for hierarchical fallback
  size?: number;
  className?: string;
}

/**
 * Simple file icon component using VSCode icons
 * Maps file extensions to professional VSCode file icons
 */
const FileIcon: React.FC<FileIconProps> = ({ 
  filename, 
  entityType,
  size = 24, 
  className = '' 
}) => {
  
  
  // For media files (images, videos, audio, archives), delegate to iconService
  // iconService has Priority 0 checks that ensure consistent icons everywhere
  const shouldUseIconService = (): boolean => {
    if (!entityType) return false;
    
    // Delegate all media files to iconService for consistency
    return entityType.startsWith('File.Image') || 
           entityType.startsWith('File.Video') || 
           entityType.startsWith('File.Audio') || 
           entityType.startsWith('File.Archive');
  };
  
  // Delegate to iconService for media files
  if (shouldUseIconService()) {
    const entityForIcon = { 
      _id: 'temp-icon-id', 
      entityType, 
      name: filename 
    };
    const iconSize = size <= 16 ? "small" : "medium";
    
    return (
      <Icon 
        entity={entityForIcon as any}
        size={iconSize}
        className={className}
      />
    );
  }
  
  
  // Smart icon resolution for hierarchical entity types (VSCode icons)
  const getIconName = (): string => {
    // 1. Try hierarchical entity type exact matches first
    if (entityType) {
      const entityTypeMap: Record<string, string> = {
        // Documents (VSCode icons available)
        'File.Document.PDF': 'file_type_pdf',
        'File.Document.Word': 'file_type_word',
        'File.Document.Text': 'file_type_text',
        'File.Document.Markdown': 'file_type_markdown',
        
        // Data files (VSCode icons available)
        'File.Data.CSV': 'file_type_csv',
        'File.Data.JSON': 'file_type_json',
        'File.Data.XML': 'file_type_xml',
        'File.Data.YAML': 'file_type_yaml',
        'File.Data.SQL': 'file_type_sql',
        
        // Spreadsheets (VSCode icons available)
        'File.Spreadsheet.Excel': 'file_type_excel',
        
        // Code files (VSCode icons available)
        'File.Code.JavaScript': 'file_type_js',
        'File.Code.TypeScript': 'file_type_typescript',
        'File.Code.HTML': 'file_type_html',
        'File.Code.CSS': 'file_type_css',
        'File.Code.Python': 'file_type_python',
      };
      
      const exactMatch = entityTypeMap[entityType];
      if (exactMatch) {
        return exactMatch;
      }
      
      // 2. Try category-based matching
      if (entityType.includes('CSV')) return 'file_type_csv';
      if (entityType.includes('PDF')) return 'file_type_pdf';
      if (entityType.includes('JSON')) return 'file_type_json';
      if (entityType.includes('JavaScript')) return 'file_type_js';
      if (entityType.includes('TypeScript')) return 'file_type_typescript';
      if (entityType.includes('HTML')) return 'file_type_html';
      if (entityType.includes('CSS')) return 'file_type_css';
      if (entityType.includes('Python')) return 'file_type_python';
      if (entityType.includes('XML')) return 'file_type_xml';
      if (entityType.includes('Excel')) return 'file_type_excel';
    }
    
    // 3. Try file extension fallback
    const lowerFilename = filename.toLowerCase();
    
    // Images
    if (lowerFilename.endsWith('.png') || lowerFilename.endsWith('.jpg') || 
        lowerFilename.endsWith('.jpeg') || lowerFilename.endsWith('.gif') || 
        lowerFilename.endsWith('.webp') || lowerFilename.endsWith('.bmp') || 
        lowerFilename.endsWith('.tiff')) return 'file_type_image';
    if (lowerFilename.endsWith('.svg')) return 'file_type_svg';
    
    // Documents
    if (lowerFilename.endsWith('.pdf')) return 'file_type_pdf';
    if (lowerFilename.endsWith('.doc') || lowerFilename.endsWith('.docx')) return 'file_type_word';
    if (lowerFilename.endsWith('.txt')) return 'file_type_text';
    if (lowerFilename.endsWith('.md') || lowerFilename.endsWith('.markdown')) return 'file_type_markdown';
    
    // Data files
    if (lowerFilename.endsWith('.csv')) return 'file_type_csv';
    if (lowerFilename.endsWith('.json')) return 'file_type_json';
    if (lowerFilename.endsWith('.xml')) return 'file_type_xml';
    if (lowerFilename.endsWith('.yaml') || lowerFilename.endsWith('.yml')) return 'file_type_yaml';
    if (lowerFilename.endsWith('.sql')) return 'file_type_sql';
    
    // Spreadsheets
    if (lowerFilename.endsWith('.xlsx') || lowerFilename.endsWith('.xls')) return 'file_type_excel';
    
    // Code files
    if (lowerFilename.endsWith('.js')) return 'file_type_js';
    if (lowerFilename.endsWith('.ts') || lowerFilename.endsWith('.tsx')) return 'file_type_typescript';
    if (lowerFilename.endsWith('.html') || lowerFilename.endsWith('.htm')) return 'file_type_html';
    if (lowerFilename.endsWith('.css')) return 'file_type_css';
    if (lowerFilename.endsWith('.py')) return 'file_type_python';
    if (lowerFilename.endsWith('.java')) return 'file_type_java';
    if (lowerFilename.endsWith('.php')) return 'file_type_php';
    if (lowerFilename.endsWith('.rb')) return 'file_type_ruby';
    if (lowerFilename.endsWith('.go')) return 'file_type_go';
    if (lowerFilename.endsWith('.rs')) return 'file_type_rust';
    
    // Media files
    if (lowerFilename.endsWith('.mp4') || lowerFilename.endsWith('.avi') || 
        lowerFilename.endsWith('.mov') || lowerFilename.endsWith('.mkv')) return 'file_type_video';
    if (lowerFilename.endsWith('.mp3') || lowerFilename.endsWith('.wav') || 
        lowerFilename.endsWith('.flac')) return 'file_type_audio';
    
    // Archives
    if (lowerFilename.endsWith('.zip')) return 'file_type_zip';
    if (lowerFilename.endsWith('.rar') || lowerFilename.endsWith('.tar') || 
        lowerFilename.endsWith('.gz')) return 'file_type_archive';
    
    // 4. Try vscode-icons-js as final attempt
    try {
      const iconInfo = getIconForFile(filename);
      if (iconInfo && iconInfo !== 'default_file') {
        return iconInfo;
      }
    } catch (error) {
      // Fallback silently
    }
    
    return 'default_file';
  };
  
  // Map vscode icon names to our downloaded files
  const getIconPath = (iconName: string): string => {
    const iconMap: Record<string, string> = {
      // Available VSCode icons (matching /vscode-icons/ directory)
      'file_type_pdf': 'pdf',
      'file_type_word': 'word',
      'file_type_csv': 'csv',
      'file_type_json': 'json',
      'file_type_js': 'js',
      'file_type_typescript': 'ts',
      'file_type_html': 'html',
      'file_type_css': 'css',
      'file_type_python': 'py',
      'file_type_xml': 'xml',
      'file_type_excel': 'xlsx',
      
      // Default fallback
      'default_file': 'default'
    };
    
    const mappedName = iconMap[iconName] || 'default';
    return `/vscode-icons/${mappedName}.svg`;
  };
  
  const iconName = getIconName();
  const iconPath = getIconPath(iconName);
  
  // Use Phosphor icon for unknown/default files instead of SVG
  // Check if the icon path would use default.svg
  if (iconPath === '/vscode-icons/default.svg') {
    const FileIcon = PhosphorIcons.File;
    
    return (
      <span 
        className={className}
        style={{
          position: 'relative',
          display: 'inline-block',
          width: `${size}px`,
          height: `${size}px`,
          verticalAlign: 'middle',
          flexShrink: 0
        }}
      >
        {/* White fill layer */}
        <FileIcon 
          weight="fill"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            color: '#ffffff'
          }}
        />
        {/* Grey border layer */}
        <FileIcon 
          weight="regular"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            color: '#6b7280'
          }}
        />
      </span>
    );
  }
  
  return (
    <img 
      src={iconPath}
      className={className}
      alt={`${filename} file icon`}
      onError={(e) => { 
        // Fallback to default icon if specific icon fails to load
        const target = e.currentTarget;
        if (target.src !== '/vscode-icons/default.svg') {
          target.src = '/vscode-icons/default.svg'; 
        }
      }}
      style={{ 
        display: 'inline-block',
        verticalAlign: 'middle',
        width: `${size}px`,
        height: `${size}px`,
        flexShrink: 0
      }}
    />
  );
};

export default FileIcon;